import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import AddAnotherButton from "../../components/AddAnotherButton";

// Import your service functions
import {
  updateEducationLevel,
  deleteEducationLevel,
  getEducationLevelById,
  getEducationLevelSubclass,
} from "../../services/educationLevelServices";

// Import your icons
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import editIcon from "../../assets/edit_icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import SectionDivider from "../../components/SectionDivider";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const EducationLevelInfoModal = ({ educationLevel, onClose, onUpdate }) => {
  const { t } = useTranslation("general");

  // State variables
  const [isEditing, setIsEditing] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Form fields
  const [educationLevelFields, setEducationLevelFields] = useState({
    name: "",
    price: "",
  });
  const [subclasses, setSubclasses] = useState([]);
  const [subclassFields, setSubclassFields] = useState([]);
  const [nextSubclassId, setNextSubclassId] = useState(0); // For generating IDs for new subclasses

  // Initialize form fields when component mounts or educationLevel changes
  useEffect(() => {
    if (educationLevel) {
      getEducationLevel();
    }
  }, [educationLevel]);

  // Handle input changes for education level
  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setEducationLevelFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error when user starts typing
    if (error) {
      setError(false);
      setErrorMessage("");
    }
  };

  // Handle input changes for subclasses
  const handleSubclassInputChange = (e, index, fieldName) => {
    const value = e.target.value;
    setSubclassFields((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [fieldName]: value,
      };
      return updated;
    });

    // Clear error when user starts typing
    if (error) {
      setError(false);
      setErrorMessage("");
    }
  };

  // Add new subclass
  const handleAddSubclass = () => {
    setSubclassFields((prev) => [
      ...prev,
      {
        id: `new_${nextSubclassId}`, // Use a temporary ID for new subclasses
        name: "",
        studentCount: 0,
        isNew: true, // Flag to identify new subclasses
      },
    ]);
    setNextSubclassId((prev) => prev + 1);
  };

  // Handle save education level
  const handleSaveEducationLevel = async () => {
    // Validation for education level
    if (!educationLevelFields.name.trim()) {
      setError(true);
      setErrorMessage(t("inputFieldCannotBeEmpty"));
      return;
    }

    if (
      !educationLevelFields.price ||
      isNaN(educationLevelFields.price) ||
      Number(educationLevelFields.price) < 0
    ) {
      setError(true);
      setErrorMessage(t("pleaseEnterValidPrice"));
      return;
    }

    // Validation for subclasses
    for (let i = 0; i < subclassFields.length; i++) {
      const subclass = subclassFields[i];
      if (!subclass.name.trim()) {
        setError(true);
        setErrorMessage(
          `${t("subclass")} ${i + 1}: ${t("inputFieldCannotBeEmpty")}`
        );
        return;
      }
    }

    try {
      setLoadingModal(true);
      setError(false);

      const updateData = {
        name: educationLevelFields.name.trim(),
        price: Number(educationLevelFields.price),
        subClasses: subclassFields.map((subclass) => ({
          ...(subclass.isNew ? {} : { id: subclass.id }), // Only include ID for existing subclasses
          name: subclass.name.trim(),
        })),
      };

      const response = await updateEducationLevel(
        educationLevel.id,
        updateData
      );

      if (response?.status === 200) {
        const data = response.data;
        setEducationLevelFields({
          name: data.schoolClass.name,
          price: data.schoolClass.price,
        });
        setIsEditing(false);
        onUpdate();

        // Refresh the data to get the updated subclasses with proper IDs
        await getEducationLevel();
      }
    } catch (error) {
      console.error("Error updating education level:", error);
      setError(true);
      setErrorMessage(
        error.response?.data?.message || t("errorUpdatingEducationLevel")
      );
    } finally {
      setLoadingModal(false);
    }
  };

  // Handle delete education level
  const handleDeleteEducationLevel = async () => {
    try {
      setLoadingModal(true);
      setError(false);

      const response = await deleteEducationLevel(educationLevel.id);

      if (response?.status === 200) {
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error("Error deleting education level:", error);
      setError(true);
      setErrorMessage(
        error.response?.data?.message ||
          t("cannotDeleteClassWithActiveStudents")
      );
    } finally {
      setLoadingModal(false);
      setShowDeleteConfirmation(false);
    }
  };

  const getEducationLevel = async () => {
    try {
      setLoading(true);
      const response = await getEducationLevelSubclass(educationLevel.id);

      if (response.status === 200) {
        const data = response.data;
        setEducationLevelFields({
          name: data.schoolClass.name || "",
          price: data.schoolClass.price || "",
        });
        setSubclasses(response.data.subClasses);

        // Initialize subclass fields for editing
        const initialSubclassFields = response.data.subClasses.map(
          (subclass) => ({
            id: subclass.id,
            name: subclass.name || "",
            studentCount: subclass.studentCount || 0,
            isNew: false,
          })
        );

        setSubclassFields(initialSubclassFields);

        // Set the next ID for new subclasses
        const maxId = response.data.subClasses.reduce(
          (max, subclass) =>
            Math.max(max, typeof subclass.id === "number" ? subclass.id : 0),
          0
        );
        setNextSubclassId(maxId + 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isEditing) {
      // Reset form fields to original values
      setEducationLevelFields({
        name: educationLevel.name || "",
        price: educationLevel.price || "",
      });

      // Reset subclass fields to original values (remove new subclasses)
      setSubclassFields(
        subclasses.map((subclass) => ({
          id: subclass.id,
          name: subclass.name || "",
          studentCount: subclass.studentCount || 0,
          isNew: false,
        }))
      );

      setIsEditing(false);
      setError(false);
      setErrorMessage("");
    } else {
      onClose();
    }
  };

  if (!educationLevel) return null;

  return (
    <>
      <Modal
        title={isEditing ? t("editEducationLevel") : t("educationLevelInfo")}
        onClose={handleCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="edit-user-actions">
              {isEditing ? (
                <>
                  <CustomButton
                    onClick={handleSaveEducationLevel}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancel}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                  <CustomButton
                    variant="red"
                    title={t("delete")}
                    onClick={() => setShowDeleteConfirmation(true)}
                    icon={deleteIcon}
                    iconSize={16}
                  />
                </>
              ) : (
                <>
                  <CustomButton
                    variant="blank"
                    title={t("edit")}
                    onClick={() => setIsEditing(true)}
                    icon={editIcon}
                    iconSize={18}
                  />
                </>
              )}
            </section>
          </>
        }
      >
        {error && <ErrorBox>{errorMessage}</ErrorBox>}

        {loading ? (
          <Loader />
        ) : (
          <div>
            <SectionDivider title="educationLevel" />
            <section>
              <div>
                {isEditing ? (
                  <div className="edit-user-entity">
                    <span>{t("educationStageName")}: </span>
                    <CustomInput
                      name="name"
                      value={educationLevelFields.name || ""}
                      onChange={(e) => handleInputChange(e, "name")}
                      errorText={
                        error && !educationLevelFields.name.trim()
                          ? t("inputFieldCannotBeEmpty")
                          : ""
                      }
                    />
                  </div>
                ) : (
                  <div className="view-user-entity">
                    <p variant="body1">{t("educationStageName")}:</p>
                    <p variant="body1">{educationLevelFields?.name}</p>
                  </div>
                )}
              </div>
            </section>

            <section>
              <div>
                {isEditing ? (
                  <div className="edit-user-entity">
                    <span>{t("price")}: </span>
                    <CustomInputWithUnit
                      unit={"IQD"}
                      name="price"
                      type="money"
                      min="0"
                      step="0.01"
                      value={educationLevelFields.price || ""}
                      onChange={(e) => handleInputChange(e, "price")}
                      errorText={
                        error &&
                        (!educationLevelFields.price ||
                          isNaN(educationLevelFields.price))
                          ? t("pleaseEnterValidPrice")
                          : ""
                      }
                    />
                  </div>
                ) : (
                  <div className="view-user-entity">
                    <p variant="body1">{t("price")}:</p>
                    <p variant="body1">
                      {educationLevelFields?.price} {t("IQD")}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* SUBCLASSES */}
            <SectionDivider title="subclasses" />
            <div className="o-column o-column--gap-size_400">
              {isEditing
                ? // Editing mode - show input fields
                  subclassFields?.map((subclass, index) => (
                    <div
                      key={subclass.id || index}
                      className="o-row o-row--gap-size_500"
                    >
                      <div className="edit-user-entity">
                        <span>
                          {t("subclass")} {index + 1}:{" "}
                        </span>
                        <CustomInput
                          title={`${t("name")}:`}
                          name={`subclass-name-${index}`}
                          value={subclass.name || ""}
                          onChange={(e) =>
                            handleSubclassInputChange(e, index, "name")
                          }
                          placeholder={t("subclassName")}
                          errorText={
                            error && !subclass.name.trim()
                              ? t("inputFieldCannotBeEmpty")
                              : ""
                          }
                        />
                      </div>
                      <div className="view-user-entity">
                        <p variant="body1">
                          ({t("numberOfStudents")}: {subclass.studentCount})
                        </p>
                      </div>
                    </div>
                  ))
                : // View mode - show read-only data
                  subclasses?.map((subclass, index) => (
                    <div
                      key={subclass.id || index}
                      className="o-row o-row--gap-size_500"
                    >
                      <span>{t("subclass")}:</span>
                      <div className="o-row o-row--gap-size_400">
                        <span className="t-bold_bold u-width--min-width_50">
                          {subclass.name}
                        </span>
                        <span>
                          ({t("numberOfStudents")}: {subclass.studentCount})
                        </span>
                      </div>
                    </div>
                  ))}
            </div>

            {/* Add Another Button - Only show in editing mode */}
            {isEditing && (
              <div className="o-row o-row--horizontal-align_end u-width_full-width u-padding--bottom_500 u-padding--top_200">
                <AddAnotherButton
                  onClick={handleAddSubclass}
                  title={t("addSubclass")}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => handleDeleteEducationLevel()}
          customMessage={t("deleteEducationLevel")}
          closeTitle="cancel"
          confirmTitle="confirm"
          loading={loadingModal}
          cancel
            confirmIconType="whiteCheck"

        />
      )}
    </>
  );
};

export default EducationLevelInfoModal;
